import {
  Amenity,
  MealPlanKind,
  type RoomInfoProducts,
  type RoomProduct,
  type RoomInfo,
} from "@b2bportal/lodging-api";

const BREAKFAST_AMENITIES: ReadonlySet<string> = new Set([
  Amenity.FreeBreakfast,
  Amenity.BreakfastIncluded,
]);

export const BREAKFAST_MEAL_PLAN_KINDS: ReadonlySet<string> = new Set([
  MealPlanKind.Breakfast,
  MealPlanKind.BreakfastFor1,
  MealPlanKind.BreakfastFor2,
]);

export const isFreeBreakfastAvailableInRoomRate = (
  product: RoomProduct
): boolean =>
  !!product?.amenities?.some((a) => BREAKFAST_AMENITIES.has(a.amenity));

export const isFreeBreakfastMealPlanAvailableInRoomRate = (
  product: RoomProduct
): boolean =>
  product?.mealPlan?.kind != null &&
  BREAKFAST_MEAL_PLAN_KINDS.has(product.mealPlan.kind);

export const isFreeBreakfastAvailableInRoomLevelAmenities = (
  roomInfo: RoomInfo
): boolean =>
  !!roomInfo?.amenities?.some((a) => BREAKFAST_AMENITIES.has(a.amenity));

/**
 * Returns true if free breakfast is available in ALL room products,
 * determined by room-level amenities, rate-level amenities, or
 * breakfast meal plans (Breakfast, BreakfastFor1, BreakfastFor2).
 */
export const isFreeBreakfastAvailableInAllRoomProducts = (
  room: RoomInfoProducts
): boolean => {
  if (!room?.roomInfo) return false;
  if (isFreeBreakfastAvailableInRoomLevelAmenities(room.roomInfo)) {
    return true;
  }

  const { products } = room;
  if (products.length === 0) return false;

  return products.every(
    (p) =>
      isFreeBreakfastAvailableInRoomRate(p) ||
      isFreeBreakfastMealPlanAvailableInRoomRate(p)
  );
};

/**
 * Returns true if ALL rooms sourced for a hotel have free breakfast
 * available across all their rates.
 */
export const allHotelRoomsHaveFreeBreakfast = (
  rooms: RoomInfoProducts[]
): boolean =>
  rooms.length > 0 && rooms.every(isFreeBreakfastAvailableInAllRoomProducts);
